import { type DeltaPanelRow, mergeRows } from "./MeasureDeltaPanel.helpers";
import { useEffect, useId, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { LabelWithTooltip } from "./MetricTooltips";

export interface MeasureDeltaPanelProps {
  open: boolean;
  title: string;
  subtitle?: string;
  current: DeltaPanelRow[];
  prior: DeltaPanelRow[];
  onClose: () => void;
}

function fmtPct(rate: number | null | undefined): string {
  if (rate == null) return "—";
  return `${Math.round(rate * 100)}%`;
}

function fmtDelta(
  deltaPct: number | null,
  hasCurrent: boolean,
  hasPrior: boolean,
): { text: string; tone: "up" | "down" | "flat" | "muted" } {
  if (!hasCurrent) return { text: "dropped", tone: "muted" };
  if (!hasPrior) return { text: "new", tone: "muted" };
  if (deltaPct == null) return { text: "—", tone: "muted" };
  const rounded = Math.round(deltaPct);
  if (rounded === 0) return { text: "↔ 0 pts", tone: "flat" };
  const up = rounded > 0;
  return {
    text: `${up ? "↑" : "↓"} ${Math.abs(rounded)} pts`,
    tone: up ? "up" : "down",
  };
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function MeasureDeltaPanel({
  open,
  title,
  subtitle,
  current,
  prior,
  onClose,
}: MeasureDeltaPanelProps) {
  const titleId = useId();
  const descId = useId();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  const rows = useMemo(() => mergeRows(current, prior), [current, prior]);

  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    if (panel) {
      const focusables = panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      const target = focusables[0] ?? panel;
      target.focus();
    }
    return () => {
      previouslyFocused.current?.focus?.();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === "Tab") {
        const panel = panelRef.current;
        if (!panel) return;
        const focusables = Array.from(
          panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
        ).filter((el) => !el.hasAttribute("disabled"));
        if (focusables.length === 0) {
          e.preventDefault();
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey) {
          if (active === first || !panel.contains(active)) {
            e.preventDefault();
            last?.focus();
          }
        } else {
          if (active === last || !panel.contains(active)) {
            e.preventDefault();
            first?.focus();
          }
        }
      }
    }
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [open, onClose]);

  if (!open) return null;

  const toneClass = (tone: "up" | "down" | "flat" | "muted") =>
    tone === "up"
      ? "bg-primary/15 text-primary"
      : tone === "down"
        ? "bg-destructive/15 text-destructive"
        : "bg-muted text-muted-foreground";

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/50 animate-dialog-backdrop"
      onClick={onClose}
      role="presentation"
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        {...(subtitle ? { "aria-describedby": descId } : {})}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className="h-full w-full max-w-xl border-l border-border bg-card shadow-xl animate-dialog-panel flex flex-col outline-none"
      >
        <div className="px-5 py-4 border-b border-border flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2
              id={titleId}
              className="text-lg font-semibold text-foreground truncate"
            >
              {title}
            </h2>
            {subtitle ? (
              <p
                id={descId}
                className="mt-0.5 text-xs text-muted-foreground"
              >
                {subtitle}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded p-1.5 text-muted-foreground hover:bg-muted/40 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="Close"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {rows.length === 0 ? (
            <p className="p-5 text-sm text-muted-foreground italic">
              No measure data to compare in this period.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="text-left font-medium px-4 py-2">Measure / label</th>
                  <th className="text-right font-medium px-3 py-2">
                    <LabelWithTooltip
                      metricId="current_rate"
                      className="inline-flex items-center gap-1 justify-end"
                    >
                      Current
                    </LabelWithTooltip>
                  </th>
                  <th className="text-right font-medium px-3 py-2">
                    <LabelWithTooltip
                      metricId="prior_rate"
                      className="inline-flex items-center gap-1 justify-end"
                    >
                      Prior
                    </LabelWithTooltip>
                  </th>
                  <th className="text-right font-medium px-4 py-2">
                    <LabelWithTooltip
                      metricId="delta_pp"
                      className="inline-flex items-center gap-1 justify-end"
                    >
                      Δ
                    </LabelWithTooltip>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const delta = fmtDelta(
                    row.deltaPct,
                    !!row.current,
                    !!row.prior,
                  );
                  return (
                    <tr
                      key={row.key}
                      className="border-t border-border align-top"
                    >
                      <td className="px-4 py-2">
                        <div className="font-medium text-foreground">
                          {row.measureLabel}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {row.labelName}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        <div className="text-foreground">
                          {fmtPct(row.current?.rate)}
                        </div>
                        {row.current ? (
                          <div className="text-xs text-muted-foreground">
                            n={row.current.calls.toLocaleString()}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        <div className="text-foreground">
                          {fmtPct(row.prior?.rate)}
                        </div>
                        {row.prior ? (
                          <div className="text-xs text-muted-foreground">
                            n={row.prior.calls.toLocaleString()}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <span
                          className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium tabular-nums ${toneClass(delta.tone)}`}
                        >
                          {delta.text}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
